import type { SupabaseClient } from '../../lib/supabase';
import type {
  CreateProjectInput,
  GrantableRole,
  Project,
  ProjectListItem,
  ProjectMemberWithProfile,
  ProjectRole,
  ProjectSection,
  ProjectStatus,
  ProjectTemplate,
  ProjectWorkspace,
  TapPerson,
} from './types';

/**
 * Database access for the project foundation.
 *
 * Every read below is already filtered by Row Level Security; the client
 * cannot widen it. Nothing here is trusted for authorization, and no query
 * loads project data at application startup.
 */

const PROJECT_LIST_COLUMNS =
  'id, name, description, visibility, owner_id, status, category, due_date, updated_at';

function fail(message: string): never {
  throw new Error(message);
}

/** Projects the caller may see, active only, most recently updated first. */
export async function fetchProjects(client: SupabaseClient): Promise<ProjectListItem[]> {
  const { data, error } = await client
    .from('projects')
    .select(PROJECT_LIST_COLUMNS)
    .eq('is_archived', false)
    // Personal spaces surface through their own menu entry, never here.
    .eq('is_personal', false)
    // Rooms (Drop 3a, plan-drop3-dms-groups.md decision D7) are projects for
    // Discussion's sake only. They have no tasks, no sections and no board,
    // so an empty card in every project list is the only thing showing one
    // here would ever accomplish.
    .eq('is_room', false)
    .order('updated_at', { ascending: false });

  if (error) {
    fail(error.message);
  }
  return data ?? [];
}

/**
 * Edits to the project record itself (name, description, visibility, and
 * the project-level workflow status). Zero rows back means the row policy
 * refused the change.
 */
export async function updateProject(
  client: SupabaseClient,
  projectId: string,
  changes: Partial<
    Pick<Project, 'name' | 'description' | 'visibility' | 'status' | 'category' | 'due_date'>
  >,
): Promise<Project> {
  const { data, error } = await client
    .from('projects')
    .update(changes)
    .eq('id', projectId)
    .select('*')
    .maybeSingle();

  if (error) {
    fail(error.message);
  }
  if (!data) {
    fail('project_not_permitted');
  }
  return data;
}

/**
 * The caller's private Personal project, created on first use. Returns its
 * id; the personal page then loads it like any other project workspace.
 */
export async function ensurePersonalProject(client: SupabaseClient): Promise<string> {
  const { data, error } = await client.rpc('ensure_personal_project');
  if (error) {
    fail(error.message);
  }
  if (typeof data !== 'string' || data === '') {
    fail('personal_project_unavailable');
  }
  return data;
}

/** The curated category list, alphabetical. TAP-only by policy. */
export async function fetchProjectCategories(client: SupabaseClient): Promise<string[]> {
  const { data, error } = await client
    .from('project_categories')
    .select('name')
    .order('name', { ascending: true });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data.map((row) => row.name) : [];
}

/** Adds a category. The citext unique index refuses case-variant twins. */
export async function createProjectCategory(
  client: SupabaseClient,
  input: { name: string; createdBy: string },
): Promise<void> {
  const { error } = await client
    .from('project_categories')
    .insert({ name: input.name.trim(), created_by: input.createdBy });
  if (error) {
    fail(error.message.includes('project_categories_name_key') ? 'category_exists' : error.message);
  }
}

/** Manager-gated archive; the function refuses non-managers. */
export async function archiveProject(client: SupabaseClient, projectId: string): Promise<void> {
  const { error } = await client.rpc('archive_project', { p_project: projectId });
  if (error) {
    fail(error.message);
  }
}

/** Active template identities for the creation form. */
export async function fetchProjectTemplates(client: SupabaseClient): Promise<ProjectTemplate[]> {
  const { data, error } = await client
    .from('project_templates')
    .select('*')
    .eq('is_active', true)
    .order('name', { ascending: true });

  if (error) {
    fail(error.message);
  }
  return data ?? [];
}

/**
 * How much each template actually builds.
 *
 * Counted from the blueprint tables rather than written into the labels by
 * hand. The blueprints are editable data — that was the point of keeping them
 * in tables — so a hardcoded "adds 3 tasks" would start lying the first time
 * somebody edits a row, and lying confidently is worse than saying nothing.
 *
 * Both tables are tiny (tens of rows), so this reads them whole and counts in
 * memory rather than asking the database for a grouped count per key.
 */
export interface TemplateBuilds {
  sections: number;
  tasks: number;
  /** Pipeline stages, since 20261107000100. */
  stages: number;
  /** Custom fields, same. */
  fields: number;
}

export async function fetchTemplateBlueprintCounts(
  client: SupabaseClient,
): Promise<Record<string, TemplateBuilds>> {
  // One view rather than four selects. The four tables have different shapes
  // and no browser should have to know that fields are counted one way and
  // their options another — see project_template_summary.
  const { data, error } = await client
    .from('project_template_summary')
    .select('template_key, sections, tasks, stages, fields');

  if (error) {
    fail(error.message);
  }

  // Every column of a VIEW is nullable in the generated types, whatever the
  // view actually guarantees. Narrowed here rather than asserted: a row that
  // somehow arrived without a key is dropped, and a missing count reads as
  // zero, so the worst case is a label that under-promises.
  const counts: Record<string, TemplateBuilds> = {};
  for (const row of data ?? []) {
    if (row.template_key === null) {
      continue;
    }
    counts[row.template_key] = {
      sections: row.sections ?? 0,
      tasks: row.tasks ?? 0,
      stages: row.stages ?? 0,
      fields: row.fields ?? 0,
    };
  }
  return counts;
}

/**
 * What a template option should say it does.
 *
 * A template that builds nothing says so plainly rather than saying nothing at
 * all: "Blank Project" alone leaves people guessing whether it is the empty one.
 *
 * STAGES AND FIELDS COUNT TOO. They did not, and the consequence was that the
 * Social Media template — eight pipeline stages and four fields, and no
 * sections at all because Format and Origin became fields precisely so they
 * would stop being sections — advertised itself as "(clean slate)". The most
 * substantial template in the list described itself as the emptiest one.
 */
export function templateOptionLabel(name: string, counts: TemplateBuilds | undefined): string {
  const parts: string[] = [];
  if (counts !== undefined) {
    // Stages first: what a board LOOKS like matters more to somebody choosing
    // a template than how many starter tasks land in it.
    if (counts.stages > 0) {
      parts.push(`${String(counts.stages)} stage${counts.stages === 1 ? '' : 's'}`);
    }
    if (counts.fields > 0) {
      parts.push(`${String(counts.fields)} field${counts.fields === 1 ? '' : 's'}`);
    }
    if (counts.sections > 0) {
      parts.push(`${String(counts.sections)} section${counts.sections === 1 ? '' : 's'}`);
    }
    if (counts.tasks > 0) {
      parts.push(`${String(counts.tasks)} task${counts.tasks === 1 ? '' : 's'}`);
    }
  }
  return parts.length === 0 ? `${name} (clean slate)` : `${name} (adds ${parts.join(', ')})`;
}

/**
 * Loads one project and its foundation records.
 *
 * Authorization is resolved by this request, not by a previously loaded list:
 * an unauthorized project returns no row and the caller renders access denied.
 */
export async function fetchProjectWorkspace(
  client: SupabaseClient,
  projectId: string,
): Promise<ProjectWorkspace | null> {
  const { data: project, error: projectError } = await client
    .from('projects')
    .select('*')
    .eq('id', projectId)
    .maybeSingle();

  if (projectError) {
    fail(projectError.message);
  }
  if (!project) {
    return null;
  }

  const [members, sections, statuses] = await Promise.all([
    fetchProjectMembers(client, projectId),
    fetchProjectSections(client, projectId),
    fetchProjectStatuses(client, projectId),
  ]);

  return { project, members, sections, statuses };
}

interface MemberRow {
  id: string;
  user_id: string;
  project_role: ProjectMemberWithProfile['project_role'];
  profiles: { display_name: string; email: string; avatar_url: string | null } | null;
}

export async function fetchProjectMembers(
  client: SupabaseClient,
  projectId: string,
): Promise<ProjectMemberWithProfile[]> {
  const { data, error } = await client
    .from('project_members')
    .select('id, user_id, project_role, profiles:user_id (display_name, email, avatar_url)')
    .eq('project_id', projectId);

  if (error) {
    fail(error.message);
  }

  return ((data ?? []) as unknown as MemberRow[]).map((row) => ({
    id: row.id,
    user_id: row.user_id,
    project_role: row.project_role,
    display_name: row.profiles?.display_name ?? '',
    avatar_url: row.profiles?.avatar_url ?? null,
    email: row.profiles?.email ?? '',
  }));
}

export async function fetchProjectSections(
  client: SupabaseClient,
  projectId: string,
): Promise<ProjectSection[]> {
  const { data, error } = await client
    .from('sections')
    .select('*')
    .eq('project_id', projectId)
    .eq('is_archived', false)
    .order('position', { ascending: true });

  if (error) {
    fail(error.message);
  }
  return data ?? [];
}

/**
 * Which status columns each section of a project draws.
 *
 * One query for the whole project rather than one per section: the board
 * switches section tabs constantly and a per-tab fetch would make the columns
 * arrive after the tasks, which reads as the board flickering.
 *
 * Sections with no configuration produce no rows at all — absent and empty are
 * the same thing here, and both mean "draw everything". See the migration.
 */
export async function fetchSectionStatuses(
  client: SupabaseClient,
  projectId: string,
): Promise<{ section_id: string; status_id: string }[]> {
  const { data, error } = await client
    .from('section_statuses')
    .select('section_id, status_id, sections!inner(project_id)')
    .eq('sections.project_id', projectId);
  if (error) {
    // Not fatal. Every section falls back to drawing every column, which is
    // exactly the behaviour before this feature existed — a board that loads
    // slightly wrong beats a board that does not load.
    return [];
  }
  return (data ?? []).map((row) => ({ section_id: row.section_id, status_id: row.status_id }));
}

/** Replace a section's drawn-column list. An empty array restores "show all". */
export async function saveSectionStatuses(
  client: SupabaseClient,
  sectionId: string,
  statusIds: string[],
): Promise<void> {
  const { error } = await client.rpc('set_section_statuses', {
    p_section: sectionId,
    p_statuses: statusIds,
  });
  if (error) {
    fail(error.message);
  }
}

export async function fetchProjectStatuses(
  client: SupabaseClient,
  projectId: string,
): Promise<ProjectStatus[]> {
  const { data, error } = await client
    .from('project_statuses')
    .select('*')
    .eq('project_id', projectId)
    .eq('is_active', true)
    .order('position', { ascending: true });

  if (error) {
    fail(error.message);
  }
  return data ?? [];
}

/**
 * Creates a project through the trusted database function.
 *
 * The owner, creator, and default statuses are decided by the database from
 * auth.uid(); no ownership value is sent from the browser.
 */
export async function createProject(
  client: SupabaseClient,
  input: CreateProjectInput,
): Promise<Project> {
  const description = input.description.trim();
  // The description key is omitted rather than sent as undefined:
  // exactOptionalPropertyTypes distinguishes the two, and the database
  // default already handles an absent description.
  const args = {
    project_name: input.name.trim(),
    project_visibility: input.visibility,
    project_template_key: input.templateKey,
    ...(description === '' ? {} : { project_description: description }),
  };
  // create_project_for_me, not create_project. The wrapper applies
  // workspace_settings.allow_member_project_creation and then delegates;
  // create_project() had its EXECUTE grant revoked from `authenticated` in
  // 20261009000100, so calling it from here would now fail outright.
  const { data, error } = await client.rpc('create_project_for_me', args);

  if (error) {
    fail(error.message);
  }
  if (!data) {
    fail('project_creation_failed');
  }
  return data;
}

/**
 * Active TAP people for the add-member picker, the deal and post pickers, and
 * anywhere a person is drawn from the whole team rather than from a project's
 * member list. Row Level Security already scopes which profiles the caller
 * may read; nothing here widens that.
 *
 * avatar_url rides along so a person drawn from this list looks the same as
 * the same person drawn from project_members or the Inbox. Until 17 September
 * it was left out, and every screen that built a member-shaped row from a
 * TapPerson had to write `avatar_url: null`, so Social Studio showed initials
 * for people whose picture the rest of the Hub already had.
 */
export async function fetchActiveTapPeople(client: SupabaseClient): Promise<TapPerson[]> {
  const { data, error } = await client
    .from('profiles')
    .select('id, display_name, email, avatar_url')
    .eq('account_type', 'tap')
    .eq('is_active', true)
    .order('display_name', { ascending: true });

  if (error) {
    fail(error.message);
  }
  return data ?? [];
}

/**
 * Membership writes. Who may manage members is decided by
 * can_manage_project() in the database; owner rows are immutable and
 * ownership is never granted from the browser (project_members_guard).
 */
export async function addProjectMember(
  client: SupabaseClient,
  input: { projectId: string; userId: string; projectRole: GrantableRole; addedBy: string },
): Promise<void> {
  const { error } = await client.from('project_members').insert({
    project_id: input.projectId,
    user_id: input.userId,
    project_role: input.projectRole,
    added_by: input.addedBy,
  });

  if (error) {
    fail(error.message);
  }
}

export async function updateProjectMemberRole(
  client: SupabaseClient,
  memberId: string,
  projectRole: ProjectRole,
): Promise<void> {
  const { error } = await client
    .from('project_members')
    .update({ project_role: projectRole })
    .eq('id', memberId);

  if (error) {
    fail(error.message);
  }
}

export async function removeProjectMember(client: SupabaseClient, memberId: string): Promise<void> {
  const { error } = await client.from('project_members').delete().eq('id', memberId);
  if (error) {
    fail(error.message);
  }
}

/**
 * Database refusals worth explaining. The trigger messages are stable; the
 * unique-constraint name comes from the project foundation migration.
 */
export const MEMBER_ERROR_MESSAGES: Record<string, string> = {
  project_member_must_be_active_tap_user: 'That person cannot be added to projects.',
  project_owner_membership_is_immutable: 'The project owner cannot be changed or removed.',
  project_owner_promotion_not_supported: 'Ownership cannot be granted here.',
  project_membership_identity_is_immutable: 'A membership cannot be moved to another person.',
  project_members_project_user_key: 'That person is already a member of this project.',
};

/** Turns a membership refusal into something a person can act on. */
export function memberErrorMessage(message: string): string {
  const known = Object.keys(MEMBER_ERROR_MESSAGES).find((key) => message.includes(key));
  if (known) {
    return MEMBER_ERROR_MESSAGES[known] as string;
  }
  const lowered = message.toLowerCase();
  if (lowered.includes('permission denied') || lowered.includes('row-level security')) {
    return 'You do not have permission to manage members in this project.';
  }
  return 'That change could not be saved. Try again.';
}

export const PROJECT_ERROR_MESSAGES: Record<string, string> = {
  not_authorized_to_create_project: 'Your account is not allowed to create projects.',
  project_name_required: 'Enter a project name.',
  unknown_project_template: 'That project template is no longer available.',
  not_authenticated: 'Your session expired. Sign in again.',
  member_project_creation_disabled:
    'A TAP admin has turned off project creation for members. Ask an admin to create it, or to turn the setting back on in Settings.',
};

/**
 * Turns a database error into something a person can act on.
 *
 * ── Why the fallback shows the raw message ─────────────────────────────────
 *
 * It used to return "Could not create the project. Try again." for anything
 * not in the table above, and that sentence cost real time: when 20261009000100
 * moved the EXECUTE grant from create_project to create_project_for_me, a
 * frontend that had not been redeployed got `permission denied for function
 * create_project` from Postgres — and every person saw "Try again", tried
 * again, and got it again. The message that would have explained it in one
 * read was being thrown away to look tidy.
 *
 * So: a known code gets its sentence, and anything else is shown as the
 * database said it. An unfamiliar error somebody can paste into a message is
 * worth more than a familiar one that says nothing. Same convention as
 * cgErrorMessage.
 */
export function projectErrorMessage(message: string): string {
  const known = PROJECT_ERROR_MESSAGES[message];
  if (known !== undefined) {
    return known;
  }
  // Substring, because PostgREST decorates: the exact-key lookup above never
  // matches a message Postgres wrapped in its own prose.
  for (const [code, sentence] of Object.entries(PROJECT_ERROR_MESSAGES)) {
    if (message.includes(code)) {
      return sentence;
    }
  }
  if (message.includes('permission denied for function create_project')) {
    // Specific on purpose. This exact string means the database has the
    // Batch 10b migration and the browser is running an older build that still
    // calls the ungated function — a deploy that went out in halves.
    return 'This copy of the Hub is out of date and is calling a database function that has been replaced. Reload the page; if it persists, the latest build needs deploying.';
  }
  if (message.includes('permission denied') || message.includes('row-level security')) {
    return 'Your account is not allowed to create projects.';
  }
  return message;
}
